/**
 * ChangePasswordForm — zmiana hasła zalogowanego usera tenantowego (F7b T7b.4).
 *
 * POST /api/auth/change-password z `{currentPassword, newPassword}` (F5 T5.11).
 *
 * Walidacja:
 *  - Real-time policy hint dla nowego hasła (minLen 10 / digit / uppercase) wg
 *    `DEFAULT_PASSWORD_POLICY` z F5. Każda reguła ma indicator (✓/·).
 *  - Local check `newPassword !== confirmNewPassword` blokuje submit.
 *
 * Error mapping (response.data.error):
 *  - 401 INVALID_PASSWORD → inline alert pod polem currentPassword.
 *  - 422 POLICY_VIOLATION (codes[]) → generic alert „hasło nie spełnia wymogów".
 *  - 409 PASSWORD_REUSED → inline alert „hasło użyte niedawno".
 *  - inne → generic alert.
 *
 * Sukces (204/200 changed:true) → toast + reset form. `onSuccess` callback opcjonalny
 * (np. żeby parent mógł wywołać `refreshMe`).
 */

import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface PolicyChecks {
  minLen: boolean;
  digit: boolean;
  upper: boolean;
}

function evaluatePolicy(password: string): PolicyChecks {
  return {
    minLen: password.length >= 10,
    digit: /\d/.test(password),
    upper: /[A-Z]/.test(password),
  };
}

export interface ChangePasswordFormProps {
  onSuccess?: () => void;
}

export function ChangePasswordForm({ onSuccess }: ChangePasswordFormProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const checks = evaluatePolicy(newPassword);
  const policyOk = checks.minLen && checks.digit && checks.upper;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setCurrentError(null);
    setConfirmError(null);
    setFormError(null);

    if (newPassword !== confirmNewPassword) {
      setConfirmError(t('tenant.profile.password.errors.mismatch'));
      return;
    }
    if (!policyOk) {
      setFormError(t('tenant.profile.password.errors.policy'));
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      toast.show({
        variant: 'success',
        title: t('tenant.profile.password.toastSuccess'),
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      onSuccess?.();
    } catch (err) {
      const axErr = err as AxiosError<{ error?: string }>;
      const status = axErr.response?.status ?? null;
      const code = axErr.response?.data?.error ?? null;
      if (status === 401 && code === 'INVALID_PASSWORD') {
        setCurrentError(t('tenant.profile.password.errors.invalidCurrent'));
      } else if (status === 422 && code === 'POLICY_VIOLATION') {
        setFormError(t('tenant.profile.password.errors.policy'));
      } else if (status === 409 && code === 'PASSWORD_REUSED') {
        setFormError(t('tenant.profile.password.errors.reused'));
      } else {
        setFormError(t('tenant.profile.password.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const PolicyItem = ({ ok, label }: { ok: boolean; label: string }) => (
    <li
      className={ok ? 'text-green-700 dark:text-green-300' : 'text-text-muted'}
      data-testid={`policy-${ok ? 'ok' : 'pending'}`}
    >
      <span aria-hidden="true" className="mr-1">
        {ok ? '✓' : '·'}
      </span>
      {label}
    </li>
  );

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
      <Input
        label={t('tenant.profile.password.currentLabel')}
        type="password"
        autoComplete="current-password"
        required
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
        disabled={submitting}
        error={currentError ?? undefined}
      />

      <div>
        <Input
          label={t('tenant.profile.password.newLabel')}
          type="password"
          autoComplete="new-password"
          required
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={submitting}
        />
        <ul
          className="mt-2 space-y-0.5 text-xs"
          aria-label={t('tenant.profile.password.policyHeading')}
        >
          <PolicyItem ok={checks.minLen} label={t('tenant.profile.password.policyMinLen')} />
          <PolicyItem ok={checks.upper} label={t('tenant.profile.password.policyUpper')} />
          <PolicyItem ok={checks.digit} label={t('tenant.profile.password.policyDigit')} />
        </ul>
      </div>

      <Input
        label={t('tenant.profile.password.confirmLabel')}
        type="password"
        autoComplete="new-password"
        required
        value={confirmNewPassword}
        onChange={(e) => setConfirmNewPassword(e.target.value)}
        disabled={submitting}
        error={confirmError ?? undefined}
      />

      {formError && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
        >
          {formError}
        </div>
      )}

      <Button
        type="submit"
        loading={submitting}
        disabled={submitting || !currentPassword || !policyOk}
        className="self-start"
      >
        {submitting ? t('tenant.profile.password.submitting') : t('tenant.profile.password.submit')}
      </Button>
    </form>
  );
}
